import { useRecoilState } from 'recoil';
import { missingInformationAtom } from '../../../recoil/atoms/missingInformationAtom';
import {
    AddressField,
    Box,
    CprField,
    EmailField,
    Grid,
    GridItem,
    PhoneField,
    validateCpr,
    validateEmail,
    validatePhone
} from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import type { FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import { useState } from 'react';
import { getDawaIdFromkvhxUrl } from '../../../utils/addressUtil';
import { isContainingDuplicateCPR } from '../configs/ValidateMissingDTOConfig';
import { replaceAllCharactersButNumbers } from '../../../utils/stringUtil';
import CustomerStopModal from '../../../shared_components/CustomerStopModal/CustomerStopModal';
// eslint-disable-next-line max-len
import type { AccidentYoungAndOtherPersonMissingInformationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation';
import { FieldBox } from './missingInformationCoverables/FieldBox';

const AccidentOtherPersonMissingInformationForm = ({
    accidentOtherPersonMissingInformation
}: {
    accidentOtherPersonMissingInformation: AccidentYoungAndOtherPersonMissingInformationDTO;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [missingInformation, setMissingInformation] = useRecoilState(missingInformationAtom);
    const {
        cpr,
        fixedId,
        address,
        isDummyContact,
        fullName = '',
        flavourName,
        phoneNumber,
        email
    } = accidentOtherPersonMissingInformation;
    const [cprNr, setCprNr] = useState(
        !isDummyContact && cpr ? replaceAllCharactersButNumbers(cpr) : undefined
    );
    const [showStopModal, setShowStopModal] = useState<boolean>(false);
    const isDuplicate = isContainingDuplicateCPR(missingInformation.accidentOtherPerson ?? []);
    const [isAddressValid, setIsAddressValid] = useState<boolean | undefined>(
        !isDummyContact && !!address
    );
    const [providedAddress, setProvidedAddress] = useState(isDummyContact ? undefined : address);
    const [phoneNr, setPhoneNr] = useState(isDummyContact ? undefined : phoneNumber);
    const [emailAddress, setEmailAddress] = useState(isDummyContact ? undefined : email);

    const updateMissingInformationProperty = (updateValue: object): void => {
        const updateValueWithFlavour = { flavourName, ...updateValue };
        setMissingInformation((previousState) => {
            const updateDto = previousState.accidentOtherPerson?.map(
                (item: AccidentYoungAndOtherPersonMissingInformationDTO) => {
                    if (item.fixedId !== fixedId) return item;
                    return {
                        ...item,
                        ...updateValueWithFlavour
                    };
                }
            );
            return {
                ...previousState,
                accidentOtherPerson: updateDto
            };
        });
    };

    const updateCpr = (value: string): void => {
        const formattedCPR = replaceAllCharactersButNumbers(value);
        setCprNr(formattedCPR);
        if (cpr !== formattedCPR) {
            updateMissingInformationProperty({
                cpr: formattedCPR
            });
        }
    };

    const updateAddress = (fieldEventDataProps: FieldEventDataProps): void => {
        const { address, kvhxUrl, addressInfo, status } = fieldEventDataProps;

        //  Checking for address on Bornholm
        const bornholmZipcodeLeadingDigits = '37';
        if (addressInfo.zipCode.substring(0, 2) === bornholmZipcodeLeadingDigits) {
            updateMissingInformationProperty({ address: '', dawaId: '' });
            setShowStopModal(true);
        } else {
            const dawaId = getDawaIdFromkvhxUrl(kvhxUrl);
            updateMissingInformationProperty({ address, dawaId });
        }
        setIsAddressValid(status === 'valid');
        setProvidedAddress(address);
    };

    const updatePhoneNr = (value: string): void => {
        const formattedPhoneNr = replaceAllCharactersButNumbers(value);
        setPhoneNr(formattedPhoneNr);
        updateMissingInformationProperty({
            phoneNumber: replaceAllCharactersButNumbers(value)
        });
    };

    const updateEmail = (value: string): void => {
        setEmailAddress(value);
        updateMissingInformationProperty({
            email: value
        });
    };

    const validateConditionalContact = (isPhoneField: boolean): boolean | undefined => {
        if (phoneNr === undefined && emailAddress === undefined) {
            return undefined;
        }
        if (isPhoneField) {
            return phoneNr !== undefined && phoneNr.length > 0
                ? validatePhone(phoneNr)
                : validateEmail(emailAddress);
        }
        return emailAddress !== undefined && emailAddress.length > 0
            ? validateEmail(emailAddress)
            : validatePhone(phoneNr);
    };

    return (
        <Box>
            <Grid gap={4}>
                <GridItem span={12}>
                    <h6>{flavourName}</h6>
                </GridItem>
                <FieldBox label={fullName}>
                    <CprField
                        label={getHippoText('DSS-ADDITIONAL-INFORMATION-CPRNUMBER')}
                        value={cprNr}
                        onBlur={(event) => {
                            updateCpr(event.target.value);
                        }}
                        isValid={
                            cprNr !== undefined ? validateCpr(cprNr) && !isDuplicate : undefined
                        }
                        errorText={
                            isDuplicate
                                ? getHippoText('DSS-ADDITIONAL-INFORMATION-DUPLICATE-CPR')
                                : undefined
                        }
                        required
                    />
                </FieldBox>
                <FieldBox>
                    <AddressField
                        label={getHippoText('DSS-ADDITIONAL-INFORMATION-ADDRESS')}
                        defaultValue={providedAddress}
                        fieldEvent={updateAddress}
                        isValid={providedAddress !== undefined ? isAddressValid : undefined}
                        required
                    />
                </FieldBox>
                <FieldBox
                    description={getHippoText(
                        'DSS-ADDITIONAL-INFORMATION-PHONEANDEMAIL-SUBSUBHEADER'
                    )}
                >
                    <PhoneField
                        label={getHippoText('DSS-ADDITIONAL-INFORMATION-MOBILENUMBER')}
                        value={phoneNr}
                        onBlur={(event) => {
                            updatePhoneNr(event.target.value);
                        }}
                        isValid={validateConditionalContact(true)}
                    />
                    <EmailField
                        label={getHippoText('DSS-ADDITIONAL-INFORMATION-EMAIL')}
                        value={emailAddress}
                        onBlur={(event) => {
                            updateEmail(event.target.value);
                        }}
                        isValid={validateConditionalContact(false)}
                    />
                </FieldBox>
                <CustomerStopModal
                    stopCause={'Bornholm'}
                    showModal={showStopModal}
                    closeModal={() => {
                        setShowStopModal(false);
                    }}
                />
            </Grid>
        </Box>
    );
};

export default AccidentOtherPersonMissingInformationForm;
